import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { motion, useInView } from 'framer-motion';
import { managementService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const PartnerSlider = () => {
    const partnerSwiperRef = useRef(null);
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
    const { language } = useLanguage();

    const [partners, setPartners] = useState([]);

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            const data = await managementService.getPartners();
            setPartners(data || []);
        } catch (err) {
            console.error('Partners API error:', err);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div ref={sectionRef} className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 relative mt-12 sm:mt-20 md:mt-32 pb-8 sm:pb-12 md:pb-16">
            <motion.p
                variants={fadeInUp}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className="text-[#292DD8] text-base sm:text-xl md:text-4xl mx-auto text-center font-bold px-2"
            >
                {language === 'fa' ? 'سازمان‌ها و ارگان‌ها همراه با کاریز' : 'Organizations & Partners'}
            </motion.p>

            {partners.length > 0 && (
                <div className="relative px-8 sm:px-10 md:px-0 mt-3 sm:mt-6 md:mt-14">
                    <Swiper
                        onSwiper={(swiper) => (partnerSwiperRef.current = swiper)}
                        modules={[Navigation, Autoplay]}
                        slidesPerView={3}
                        spaceBetween={10}
                        loop={true}
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        breakpoints={{
                            320: { slidesPerView: 3, spaceBetween: 8 },
                            480: { slidesPerView: 4, spaceBetween: 12 },
                            640: { slidesPerView: 5, spaceBetween: 15 },
                            768: { slidesPerView: 5, spaceBetween: 20 },
                            1024: { slidesPerView: 5, spaceBetween: 30 }
                        }}
                        className="partnerSwiper"
                    >
                        {partners.map((partner, index) => (
                            <SwiperSlide key={index}>
                                <motion.a
                                    href={partner.url || '#'}
                                    target={partner.url ? "_blank" : "_self"}
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.05, duration: 0.5 }}
                                    whileHover={{ scale: 1.05, rotateY: 5 }}
                                    className="relative h-[65px] sm:h-[100px] md:h-[197px] cursor-pointer flex items-center justify-center"
                                >
                                    <img
                                        src={partner.image}
                                        alt={`Partner ${index + 1}`}
                                        className="w-full h-full object-contain rounded-lg transition-all duration-300"
                                    />
                                </motion.a>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {partners.length > 5 && (
                        <>
                            <motion.button
                                onClick={() => partnerSwiperRef.current?.slidePrev()}
                                className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 md:-translate-x-12 rounded-full flex items-center justify-center z-10 cursor-pointer group"
                            >
                                <img src="/left.svg" alt="Previous" className="w-full h-full transition-transform group-hover:scale-110" />
                            </motion.button>

                            <motion.button
                                onClick={() => partnerSwiperRef.current?.slideNext()}
                                className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 md:translate-x-16 rounded-full flex items-center justify-center z-10 cursor-pointer group"
                            >
                                <img src="/right.svg" alt="Next" className="w-full h-full transition-transform group-hover:scale-110" />
                            </motion.button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PartnerSlider;